import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  accessApi,
  accessKeys,
  useOwnAccount,
  useOwnSessions,
  useRevokeOwnSession,
  type LoginSession,
} from "../access/api";
import {
  CODE_LENGTH,
  DISPLAY_NAME_MAX,
  DISPLAY_NAME_PROBLEM_KEY,
  PASSWORD_PROBLEM_KEY,
  displayNameProblem,
  isCodeComplete,
  looksLikeEmail,
  normalizeCode,
  normalizeDisplayName,
  passwordProblem,
} from "../access/codes";
import { isElevationCancelled, useElevation } from "../access/elevation";
import { readFailure } from "../access/failure";
import { SessionsTable } from "../access/sessions-table";

/**
 * The own account ([F-029], [F-033], [NF-008], [AC-044], [AC-063]):
 *
 *   - the display name - changed without elevation, it opens nothing; a name
 *     that is taken comes back with a free variation ([DO-012]);
 *   - the password - needs an elevated session; the other sessions end;
 *   - the mail address - the login name and the recovery path, so it needs an
 *     elevated session AND a code at the old and at the new address;
 *   - the own sessions, each ended on its own ("mein Rechner ist weg").
 *
 * Every critical request goes through `withElevation`: the dialog appears
 * only when the server asks for it.
 */
export function AccountPage(): JSX.Element {
  const { t } = useTranslation();
  const own = useOwnAccount();
  const { elevate } = useElevation();
  const account = own.data?.account;

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 data-testid="account_heading" className="text-2xl font-semibold tracking-tight">
          {t("ownAccount.title")}
        </h1>
        {own.isError ? (
          <p data-testid="account_load-error" role="alert" className="text-sm text-destructive-text">
            {t("errors.load")}
          </p>
        ) : null}
        {account ? (
          <dl data-testid="account_details" className="grid max-w-xl grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">{t("ownAccount.displayName")}</dt>
            <dd data-testid="account_display-name">{account.display_name}</dd>
            <dt className="text-muted-foreground">{t("ownAccount.email")}</dt>
            <dd data-testid="account_email" className="break-all">
              {account.email}
            </dd>
            <dt className="text-muted-foreground">{t("ownAccount.sessionEnds")}</dt>
            <dd data-testid="account_session-ends">{formatDateTime(own.data?.session_ends_at)}</dd>
            <dt className="text-muted-foreground">{t("ownAccount.elevation")}</dt>
            <dd data-testid="account_elevation" className="flex flex-wrap items-center gap-3">
              {own.data?.elevated ? (
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                  {t("ownAccount.elevatedUntil", { time: formatDateTime(own.data.elevated_until) })}
                </span>
              ) : (
                <>
                  <span>{t("ownAccount.notElevated")}</span>
                  <Button
                    data-testid="account_elevate"
                    variant="outline"
                    size="sm"
                    onClick={() => void elevate().catch(() => undefined)}
                  >
                    {t("ownAccount.elevate")}
                  </Button>
                </>
              )}
            </dd>
          </dl>
        ) : null}
      </div>

      <DisplayNameSection current={account?.display_name ?? ""} />
      <PasswordSection />
      <EmailSection current={account?.email ?? ""} />
      <OwnSessionsSection />
    </div>
  );
}

function Section({ testid, title, lead, children }: { testid: string; title: string; lead: string; children: ReactNode }): JSX.Element {
  return (
    <section data-testid={testid} className="max-w-3xl space-y-3 rounded-lg border p-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{lead}</p>
      </div>
      {children}
    </section>
  );
}

function InlineError({ testid, message }: { testid: string; message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p data-testid={testid} role="alert" className="text-sm text-destructive-text">
      {message}
    </p>
  );
}

/** [AC-044]: the name shows everywhere at once, also on existing entries - the server resolves names at display time. */
function DisplayNameSection({ current }: { current: string }): JSX.Element {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setValue(current), [current]);

  const problem = value.trim() === "" ? null : displayNameProblem(value);
  const unchanged = normalizeDisplayName(value) === current;

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (problem || unchanged) return;
    setBusy(true);
    setError(null);
    setSuggestion(null);
    try {
      const reply = await accessApi.changeDisplayName(value);
      setValue(reply.display_name);
      await queryClient.invalidateQueries({ queryKey: accessKeys.me });
      // Names are resolved at display time; the directories must refetch.
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await refresh();
      toast.success(t("ownAccount.displayNameSaved"));
    } catch (failure) {
      const read = readFailure(failure, t("errors.saveFailed"));
      setError(read.message);
      setSuggestion(read.suggestion);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section testid="account_name-section" title={t("ownAccount.displayNameTitle")} lead={t("ownAccount.displayNameLead")}>
      <form data-testid="account_name-form" className="flex flex-wrap items-end gap-3" onSubmit={(event) => void save(event)}>
        <div className="min-w-[16rem] flex-1 space-y-2">
          <Label htmlFor="account-name">{t("ownAccount.displayName")}</Label>
          <Input
            id="account-name"
            data-testid="account_name-input"
            value={value}
            maxLength={DISPLAY_NAME_MAX * 2}
            onChange={(event) => setValue(event.target.value)}
          />
        </div>
        <Button data-testid="account_name-save" type="submit" disabled={busy || unchanged || problem !== null || value.trim() === ""}>
          {t("account.save")}
        </Button>
      </form>
      {problem ? (
        <p data-testid="account_name-problem" className="text-xs text-destructive-text">
          {t(DISPLAY_NAME_PROBLEM_KEY[problem])}
        </p>
      ) : null}
      <InlineError testid="account_name-error" message={error} />
      {suggestion ? (
        <Button
          data-testid="account_name-take-suggestion"
          variant="outline"
          size="sm"
          onClick={() => {
            setValue(suggestion);
            setSuggestion(null);
            setError(null);
          }}
        >
          {t("forms.displayName.takeSuggestion", { name: suggestion })}
        </Button>
      ) : null}
    </Section>
  );
}

function PasswordSection(): JSX.Element {
  const { t } = useTranslation();
  const { withElevation } = useElevation();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const problem = password === "" ? null : passwordProblem(password, repeat === "" ? undefined : repeat);
  const ready = repeat !== "" && passwordProblem(password, repeat) === null;

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await withElevation(() => accessApi.changePassword(password));
      setPassword("");
      setRepeat("");
      await queryClient.invalidateQueries({ queryKey: accessKeys.mySessions });
      toast.success(t("ownAccount.passwordSaved"));
    } catch (failure) {
      if (!isElevationCancelled(failure)) setError(readFailure(failure, t("errors.saveFailed")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section testid="account_password-section" title={t("ownAccount.passwordTitle")} lead={t("ownAccount.passwordLead")}>
      <form data-testid="account_password-form" className="grid max-w-xl gap-3 sm:grid-cols-2" onSubmit={(event) => void save(event)}>
        <div className="space-y-2">
          <Label htmlFor="account-password">{t("ownAccount.newPassword")}</Label>
          <Input
            id="account-password"
            data-testid="account_password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="account-password-repeat">{t("ownAccount.passwordRepeat")}</Label>
          <Input
            id="account-password-repeat"
            data-testid="account_password-repeat"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <Button data-testid="account_password-save" type="submit" disabled={busy || !ready}>
            {t("ownAccount.passwordSave")}
          </Button>
        </div>
      </form>
      <p className="text-xs text-muted-foreground">{t("forms.password.hint")}</p>
      {problem ? (
        <p data-testid="account_password-problem" className="text-xs text-destructive-text">
          {t(PASSWORD_PROBLEM_KEY[problem])}
        </p>
      ) : null}
      <InlineError testid="account_password-error" message={error} />
    </Section>
  );
}

/** [F-033]: only to an address no other account holds, and with a code at BOTH addresses. */
function EmailSection({ current }: { current: string }): JSX.Element {
  const { t } = useTranslation();
  const { withElevation } = useElevation();
  const { refresh } = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [target, setTarget] = useState("");
  const [codeOld, setCodeOld] = useState("");
  const [codeNew, setCodeNew] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const request = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!looksLikeEmail(target)) return;
    setBusy(true);
    setError(null);
    try {
      await withElevation(() => accessApi.emailRequest(target.trim()));
      setCodeOld("");
      setCodeNew("");
      setStep("confirm");
    } catch (failure) {
      if (!isElevationCancelled(failure)) setError(readFailure(failure, t("errors.saveFailed")).message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!isCodeComplete(codeOld) || !isCodeComplete(codeNew)) return;
    setBusy(true);
    setError(null);
    try {
      await withElevation(() =>
        accessApi.emailConfirm({ new_email: target.trim(), code_old: normalizeCode(codeOld), code_new: normalizeCode(codeNew) }),
      );
      setStep("request");
      setTarget("");
      await queryClient.invalidateQueries({ queryKey: accessKeys.all });
      await refresh();
      toast.success(t("ownAccount.emailSaved"));
    } catch (failure) {
      if (!isElevationCancelled(failure)) setError(readFailure(failure, t("errors.saveFailed")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section testid="account_email-section" title={t("ownAccount.emailTitle")} lead={t("ownAccount.emailLead")}>
      {step === "request" ? (
        <form data-testid="account_email-form" className="flex flex-wrap items-end gap-3" onSubmit={(event) => void request(event)}>
          <div className="min-w-[16rem] flex-1 space-y-2">
            <Label htmlFor="account-email-new">{t("ownAccount.newEmail")}</Label>
            <Input
              id="account-email-new"
              data-testid="account_email-new"
              type="email"
              autoComplete="email"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
          </div>
          <Button data-testid="account_email-request" type="submit" disabled={busy || !looksLikeEmail(target)}>
            {t("ownAccount.emailRequest")}
          </Button>
        </form>
      ) : (
        <form data-testid="account_email-confirm-form" className="space-y-3" onSubmit={(event) => void confirm(event)}>
          <p data-testid="account_email-sent" className="text-sm">
            {t("ownAccount.emailSent", { old: current, target: target.trim() })}
          </p>
          <div className="grid max-w-xl gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="account-email-code-old">{t("ownAccount.codeOld")}</Label>
              <Input
                id="account-email-code-old"
                data-testid="account_email-code-old"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH + 2}
                value={codeOld}
                onChange={(event) => setCodeOld(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-email-code-new">{t("ownAccount.codeNew")}</Label>
              <Input
                id="account-email-code-new"
                data-testid="account_email-code-new"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH + 2}
                value={codeNew}
                onChange={(event) => setCodeNew(event.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="account_email-confirm"
              type="submit"
              disabled={busy || !isCodeComplete(codeOld) || !isCodeComplete(codeNew)}
            >
              {t("ownAccount.emailConfirm")}
            </Button>
            <Button
              data-testid="account_email-cancel"
              type="button"
              variant="ghost"
              onClick={() => {
                setStep("request");
                setError(null);
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      )}
      <InlineError testid="account_email-error" message={error} />
    </Section>
  );
}

/** [AC-063]: ending a session here logs that device out at its next request. */
function OwnSessionsSection(): JSX.Element {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const sessions = useOwnSessions();
  const revoke = useRevokeOwnSession();
  const [error, setError] = useState<string | null>(null);

  const end = (session: LoginSession): void => {
    setError(null);
    revoke.mutate(session.id, {
      onSuccess: () => {
        // Ending this very session is a logout.
        if (session.current) logout();
        else toast.success(t("sessions.revoked"));
      },
      onError: (failure) => setError(readFailure(failure, t("errors.generic")).message),
    });
  };

  return (
    <Section testid="account_sessions-section" title={t("ownAccount.sessionsTitle")} lead={t("ownAccount.sessionsLead")}>
      {sessions.isError ? (
        <p data-testid="account_sessions-error" role="alert" className="text-sm text-destructive-text">
          {t("errors.load")}
        </p>
      ) : sessions.data ? (
        <SessionsTable
          testid="account_sessions"
          sessions={sessions.data}
          busyId={revoke.isPending ? (revoke.variables ?? null) : null}
          onRevoke={end}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      )}
      <InlineError testid="account_sessions-revoke-error" message={error} />
    </Section>
  );
}
